import useAuth from '../hooks/useAuth';

/**
 * Service for logging frontend errors to the backend
 */
class ErrorLoggerService {
  constructor() {
    this.endpoint = '/errorLogs/frontend';
    // Dynamically determine the API URL based on the current domain
    // Use the current domain for same-domain deployment or fall back to localhost
    this.apiUrl = window.location.hostname === 'localhost' 
      ? 'http://localhost:4000' 
      : `${window.location.origin}`;
    //console.log('Frontend Error Logger initialized with API URL:', this.apiUrl);
  }

  /**
   * Log an error to the backend
   * @param {Error} error - The error object
   * @param {string} location - Where in the app the error occurred (component name, etc.)
   * @param {Object} additionalInfo - Any additional context to log
   * @param {string} token - Auth token for authenticated requests
   */
  // Flag to track if we're in the process of logging to prevent infinite loops
  #isLogging = false;
  
  async logError(error, location, additionalInfo = {}, token = null) {
    // Don't log if we're already logging (prevents recursion)
    if (this.#isLogging) {
      // Silent return to break potential loop
      return;
    }
    
    this.#isLogging = true;
    
    try {
      // Log to console (but don't use console.error to avoid recursive loops)
      const originalConsoleLog = console.log;
      originalConsoleLog(`Logging frontend error from ${location}:`, error?.message || 'Unknown error');
      
      // Early return for local development to avoid unnecessary API calls
      if (window.location.hostname === 'localhost') {
        originalConsoleLog('Skipping error logging API call in local development');
        return;
      }
      
      try {
        // Determine which endpoint to use based on authentication status
        const endpoint = token ? this.endpoint : '/errorLogs/frontend/anonymous';
        
        // If we're on production domain www.bidd.is, make sure we're using the correct API URL
        // This ensures we're sending to the proper backend endpoint and not the frontend server
        if (window.location.hostname === 'www.bidd.is' || window.location.hostname === 'bidd.is') {
          this.apiUrl = process.env.REACT_APP_BACKEND_URL || 'https://bidd-live-backend.onrender.com';
        }
        
        // Capture detailed route information
        const routeInfo = {
          fullUrl: window.location.href,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash
        };
        
        const payload = {
          message: error.message || 'Unknown frontend error',
          stack: error.stack,
          location,
          metadata: {
            type: 'frontend-error',
            url: routeInfo.fullUrl,
            route: routeInfo.pathname,
            routeInfo: routeInfo,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            ...additionalInfo
          }
        };

        const headers = {
          'Content-Type': 'application/json'
        };

        // Add authorization token if provided
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
          originalConsoleLog('Using authenticated error logging with token:', token.substring(0, 10) + '...');
        } else {
          originalConsoleLog('No auth token available, using anonymous error logging');
        }

        originalConsoleLog(`Sending error log to ${this.apiUrl}${endpoint}`);
        
        // Set a timeout to prevent hanging on connection issues
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        
        try {
          const response = await fetch(`${this.apiUrl}${endpoint}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(payload),
            credentials: 'include',
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            originalConsoleLog('Error response from server:', response.status, errorData);
          } else {
            originalConsoleLog('Error successfully logged to backend');
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          // Handle fetch errors silently to avoid infinite loops
          originalConsoleLog('Backend logging failed:', fetchError.message);
        }
      } catch (innerError) {
        // Log error but avoid using console.error to prevent recursive loops
        originalConsoleLog('Error during frontend error logging:', innerError);
      }
    } finally {
      // Always reset the logging flag
      this.#isLogging = false;
    }
  }
  
  /**
   * Create a wrapped error handler for components
   * @param {string} componentName - The name of the component 
   * @param {function} fn - The function to wrap
   * @param {string} token - Auth token
   * @returns {function} - Wrapped function that logs errors
   */
  createErrorHandler(componentName, fn, token) {
    return async (...args) => {
      try {
        return await fn(...args);
      } catch (error) {
        this.logError(
          error,
          componentName,
          { args: JSON.stringify(args) },
          token
        );
        throw error; // Re-throw so the component can handle it
      }
    };
  }
}

// Create a singleton instance
const errorLogger = new ErrorLoggerService();

/**
 * Hook to use error logging in components
 * @returns {Object} Error logging utilities
 */
export const useErrorLogger = () => {
  const { accessToken } = useAuth(); // Destructure to get accessToken directly  
  
  return {
    /**
     * Log an error
     */
    logError: (error, location, additionalInfo = {}) => 
      errorLogger.logError(error, location, {
        ...additionalInfo,
        source: 'frontend',  // Always mark as frontend source
        environment: 'frontend'
      }, accessToken),
    
    /**
     * Wrap a function with error logging
     */
    withErrorLogging: (componentName, fn) => 
      errorLogger.createErrorHandler(componentName, fn, accessToken),
      
    /**
     * Creates an error boundary fallback handler function
     */
    errorBoundaryHandler: (error, info) => {
      //console.log('Error boundary handler called with token:', accessToken ? 'available' : 'not available');
      
      errorLogger.logError(
        error,
        'ErrorBoundary',
        { 
          componentStack: info?.componentStack,
          source: 'frontend',
          environment: 'frontend'
        },
        accessToken
      );
    }
  };
};

/**
 * Global error event handler setup
 * Attaches listeners for unhandled errors and rejections
 */
export const setupGlobalErrorHandlers = () => {
  // Helper function to get auth token from localStorage
  const getAuthToken = () => {
    try {
      // Get token from localStorage directly
      const persistedState = localStorage.getItem('reduxState');
      if (persistedState) {
        const state = JSON.parse(persistedState);
        const token = state?.auth?.token;
        //console.log('Global error handler - token from localStorage:', token ? 'available' : 'null');
        return token;
      }
      return null;
    } catch (e) {
      console.error('Error retrieving auth token from localStorage:', e);
      return null;
    }
  };
  
  const handleGlobalError = (event) => {
    event.preventDefault();
    
    const error = event.error || new Error(event.message || 'Unknown error');
    const token = getAuthToken();
    
    // Capture detailed route information
    const routeInfo = {
      fullUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    };
    
    errorLogger.logError(error, 'GlobalErrorHandler', {
      type: 'uncaught-exception',
      source: 'frontend',
      environment: 'frontend',
      lineno: event.lineno,
      colno: event.colno,
      filename: event.filename,
      route: routeInfo.pathname,
      routeInfo: routeInfo
    }, token);
    
    // Returning true prevents the default browser error handling
    return true;
  };
  
  const handleRejection = (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason) || 'Unhandled Promise Rejection');
    
    const token = getAuthToken();
    
    // Capture detailed route information
    const routeInfo = {
      fullUrl: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    };
      
    errorLogger.logError(error, 'PromiseRejectionHandler', {
      type: 'unhandled-rejection',
      source: 'frontend',
      environment: 'frontend',
      route: routeInfo.pathname,
      routeInfo: routeInfo
    }, token);
  };
  
  // Attach global handlers
  window.addEventListener('error', handleGlobalError);
  window.addEventListener('unhandledrejection', handleRejection);
  
  // Return a cleanup function
  return () => {
    window.removeEventListener('error', handleGlobalError);
    window.removeEventListener('unhandledrejection', handleRejection);
  };
};

// Export the singleton instance as default
export default errorLogger;
